/* eslint-disable no-undef */
/* eslint-disable no-alert */
import { useEffect, useRef } from 'react';
import axios, { API } from 'lib/config/axios';
import useAppNavigator from 'views/rebranded-module/common/hooks/useAppNavigator';
import { mapNamedLocaleToLocale } from 'lib/data/localesMap';
import { useDispatch, useSelector } from 'react-redux';

import {
  setAppData,
  setIsCrawler,
  setHrefLangData,
  resetGlobalDataSlice,
  setDeviceType,
  setShowMembership,
  setIsSSR,
  setIsInIframe,
  setAgentLocationInfo,
} from 'redux/features/globalData-slice';
import {
  setError,
  setLoading,
  setShouldRenderPage,
  resetUISlice,
} from 'redux/features/ui-slice';
import useErrorsHandler from 'views/rebranded-module/common/hooks/useErrorsHandler';
import useCookies from 'views/common/hooks/useCookies';
import { countries } from 'lib/data/countries';
import { WL_SS_KEY } from 'views/common/components/Logical/MainWidgetWrapper/utils/constants';

const LayoutLogicWrapper = (props) => {
  const {
    layoutData,
    ssrRender,
    hreflangData,
    requireAuth,
    children,
    deviceType,
    serverError,
    options,
  } = props;
  const dispatch = useDispatch();
  const scrollPositions = useRef({});
  const isHistoryNav = useRef(false);

  const handleErrorResponse = useErrorsHandler();
  const error = useSelector((state) => state.ui.error);
  const appState = useSelector((state) => state.globalData.appState);
  const [cookies] = useCookies();
  const supressNavigationLoader = useSelector(
    (state) => state.ui.supressNavigationLoader,
  );
  const appNavigator = useAppNavigator();
  const addLangAttribute = () => {
    document
      .querySelector('html')
      .setAttribute('lang', mapNamedLocaleToLocale[appNavigator.locale]);
  };
  const redirectIfRequireAuth = (user) => {
    if (
      typeof window !== 'undefined'
      && Object.keys(appState) > 0
      && requireAuth
      && (!user || !user.id)
    ) {
      appNavigator.push('/users/sign_in');
      return true;
    }
    return false;
  };
  const initReduxStore = () => {
    dispatch(resetGlobalDataSlice());
    dispatch(resetUISlice());
  };
  if (typeof window === 'undefined') {
    initReduxStore();
  }

  const setLocationInfoData = () => {
    if (cookies.geo_location_v2) {
      const country = countries.find(
        (countryItem) => countryItem.code === cookies.geo_location_v2.country_code,
      );
      dispatch(
        setAgentLocationInfo({ ...cookies.geo_location_v2, ...country }),
      );
    }
  };

  const setReferralCodeSessionStorage = () => {
    if (appNavigator.query.referral_code) {
      sessionStorage.setItem(WL_SS_KEY, appNavigator.query.referral_code);
    }
  };
  if (
    typeof window !== 'undefined'
    && window.location !== window.parent.location
  ) {
    dispatch(setIsInIframe(window.location !== window.parent.location));
  }

  if (hreflangData) {
    dispatch(setHrefLangData(hreflangData));
  }
  if (layoutData) {
    dispatch(
      setShowMembership(
        !!layoutData.current_ip_country?.default_membership?.id,
      ),
    );
    dispatch(setIsSSR(true));
    dispatch(setAppData(layoutData));
    dispatch(setShouldRenderPage(true));
  } else {
    dispatch(setIsSSR(false));
  }
  if (ssrRender) {
    dispatch(setIsCrawler(true));
  }
  if (deviceType) {
    dispatch(setDeviceType(deviceType));
  }

  if (serverError?.statusCode && !error.statusCode) {
    dispatch(setError(serverError));
  }

  // Scroll restoration logic
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    const handlePopState = () => {
      isHistoryNav.current = true;
    };

    const handleRouteChangeStart = () => {
      // Save current scroll position before navigation
      // This is critical for both first and second navigation
      const currentPath = appNavigator.asPath;
      // Get scroll position immediately (synchronously) to ensure we capture it before navigation
      const currentScrollY = window.scrollY;
      if (currentPath && currentScrollY >= 0) {
        scrollPositions.current[currentPath] = currentScrollY;
      }
      // Also use requestAnimationFrame as backup to ensure we get the most accurate position
      // This helps in cases where scroll position might change slightly
      requestAnimationFrame(() => {
        const backupScrollY = window.scrollY;
        if (currentPath && backupScrollY >= 0) {
          scrollPositions.current[currentPath] = backupScrollY;
        }
      });
    };

    const handleRouteChangeComplete = (url) => {
      const shouldIgnoreScrolling = url.includes('/reservation');

      if (shouldIgnoreScrolling) {
        isHistoryNav.current = false;
        return;
      }
      if (isHistoryNav.current) {
        // Try to get saved position using the URL from routeChangeComplete
        // Also try appNavigator.asPath as fallback in case URLs don't match exactly
        const savedPosition = scrollPositions.current[url]
          || scrollPositions.current[appNavigator.asPath];
        if (savedPosition !== undefined && savedPosition > 0) {
          const targetPosition = savedPosition;
          const startTime = Date.now();
          // Increased max wait time for production to handle slower connections
          const maxWaitTime = 8000;
          let lastScrollHeight = document.documentElement.scrollHeight;
          let stableCount = 0;
          let mutationObserver = null;
          let resizeObserver = null;

          const attemptScroll = () => {
            const { scrollHeight } = document.documentElement;
            const maxScroll = scrollHeight - window.innerHeight;
            // Only scroll if:
            // 1. Target position is within valid range
            // 2. We have enough content (scrollHeight > targetPosition)
            // 3. Target position is reachable (maxScroll >= targetPosition)
            if (
              targetPosition >= 0
              && targetPosition <= scrollHeight
              && scrollHeight > targetPosition
              && maxScroll >= targetPosition
            ) {
              window.scrollTo(0, targetPosition);
              // Update saved position after successful scroll restoration
              // This ensures the position is saved for next navigation
              scrollPositions.current[url] = targetPosition;
              // Also save using appNavigator.asPath as fallback
              scrollPositions.current[appNavigator.asPath] = targetPosition;
              isHistoryNav.current = false;
              if (mutationObserver) {
                mutationObserver.disconnect();
              }
              if (resizeObserver) {
                resizeObserver.disconnect();
              }
              // Double-check and update position after multiple delays
              // This ensures the position is saved correctly on slower connections
              // and for subsequent navigations (first and second time)
              setTimeout(() => {
                const finalPosition = window.scrollY;
                if (finalPosition > 0) {
                  scrollPositions.current[url] = finalPosition;
                  scrollPositions.current[appNavigator.asPath] = finalPosition;
                }
              }, 100);
              // Additional check after longer delay to ensure position is saved
              // This is critical for the second navigation
              setTimeout(() => {
                const finalPosition = window.scrollY;
                if (finalPosition > 0) {
                  scrollPositions.current[url] = finalPosition;
                  scrollPositions.current[appNavigator.asPath] = finalPosition;
                }
              }, 500);
              return true;
            }
            return false;
          };

          const scrollWhenReady = () => {
            const { scrollHeight } = document.documentElement;
            const maxScroll = scrollHeight - window.innerHeight;
            const elapsed = Date.now() - startTime;

            // Check if height is stable (content fully loaded)
            if (scrollHeight === lastScrollHeight) {
              stableCount += 1;
            } else {
              stableCount = 0;
              lastScrollHeight = scrollHeight;
            }

            // Only attempt scroll if:
            // 1. Height is stable for at least 10 frames (content fully loaded)
            //    Increased for production to ensure content is fully rendered
            // 2. We have enough content to reach target position
            // 3. Target position is within reachable range
            const canScroll = stableCount >= 10
              && scrollHeight > targetPosition
              && maxScroll >= targetPosition;

            if (canScroll && attemptScroll()) {
              return;
            }

            if (elapsed < maxWaitTime) {
              requestAnimationFrame(scrollWhenReady);
            } else {
              // Position unreachable - just don't scroll at all
              isHistoryNav.current = false;
              if (mutationObserver) {
                mutationObserver.disconnect();
              }
              if (resizeObserver) {
                resizeObserver.disconnect();
              }
            }
          };

          // Use MutationObserver to detect when lazy-loaded content is added to DOM
          if (typeof MutationObserver !== 'undefined') {
            mutationObserver = new MutationObserver(() => {
              const currentScrollHeight = document.documentElement.scrollHeight;
              if (currentScrollHeight > lastScrollHeight) {
                lastScrollHeight = currentScrollHeight;
                stableCount = 0; // Reset stability counter when content changes
                attemptScroll();
              }
            });

            mutationObserver.observe(document.body, {
              childList: true,
              subtree: true,
            });
          }

          // Use ResizeObserver as additional fallback
          if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(() => {
              attemptScroll();
            });
            resizeObserver.observe(document.body);
          }

          // Longer delay to allow lazy-loaded content to render
          setTimeout(() => {
            lastScrollHeight = document.documentElement.scrollHeight;
            requestAnimationFrame(scrollWhenReady);
          }, 1500);
        } else {
          isHistoryNav.current = false;
        }
      }
    };

    // Save scroll position periodically while scrolling
    // This ensures the position is always up-to-date, especially after scroll restoration
    let scrollTimeout = null;
    const handleScroll = () => {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      scrollTimeout = setTimeout(() => {
        const currentPath = appNavigator.asPath;
        const currentScrollY = window.scrollY;
        // Only save if we have a valid scroll position and path
        // This is critical for production where scroll restoration might happen slowly
        if (currentPath && currentScrollY >= 0) {
          scrollPositions.current[currentPath] = currentScrollY;
        }
      }, 200);
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('scroll', handleScroll, { passive: true });
    appNavigator.events.on('routeChangeStart', handleRouteChangeStart);
    appNavigator.events.on('routeChangeComplete', handleRouteChangeComplete);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      appNavigator.events.off('routeChangeStart', handleRouteChangeStart);
      appNavigator.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
  }, [appNavigator]);
  // End of Scroll restoration logic

  // call Layout Data endpoint
  useEffect(() => {
    if (options?.skipLayoutFromGlobal) return;
    if (!ssrRender && !layoutData) {
      axios
        .get(API.layout)
        .then((res) => {
          if (redirectIfRequireAuth(res.data.current_user)) {
            return;
          }
          dispatch(
            setShowMembership(
              !!res.data.current_ip_country?.default_membership?.id,
            ),
          );

          dispatch(setAppData(res.data));
          if (res.data.authenticity_token) {
            localStorage.setItem(
              'authenticity_token',
              res.data.authenticity_token,
            );
          }
          dispatch(setShouldRenderPage(true));
          window.isMember = res.data.current_user?.is_member;
          window.userId = res.data.current_user?.id;
        })
        .catch((err) => {
          handleErrorResponse(err);
        });
    }
  }, []);

  // Listen for page changes after a navigation or when the query changes
  useEffect(() => {
    appNavigator.events.on('routeChangeComplete', () => {});
    return () => {
      appNavigator.events.off('routeChangeComplete', () => {});
    };
  }, [appNavigator.events]);

  useEffect(() => {
    if (!navigator.cookieEnabled) {
      alert(
        'Cookies are disabled!\nPlease enable cookies from your browser settings for best experience',
      );
    }
  }, []);
  // render LoadingSpinner and adjust lang attribute when navigating between pages
  useEffect(() => {
    const handlePageChangeSpinner = () => {
      if (error.statusCode) {
        dispatch(setError({ statusCode: false }));
      }
      addLangAttribute();
      if (!supressNavigationLoader) {
        dispatch(setLoading(true));
      } else {
        dispatch(setLoading(false));
      }
    };
    appNavigator.events.on('routeChangeStart', handlePageChangeSpinner);
    appNavigator.events.on('routeChangeComplete', () => {
      addLangAttribute();
      dispatch(setLoading(false));
    });
    appNavigator.events.on('routeChangeError', () => {
      addLangAttribute();
      dispatch(setLoading(false));
    });
    return () => {
      appNavigator.events.off('routeChangeStart', handlePageChangeSpinner);
    };
  }, [error, supressNavigationLoader]);

  // adjust lang attribute when changing locales
  useEffect(() => {
    addLangAttribute();
  }, [appNavigator.locale]);

  // If the page require authentication, redirect to home page
  useEffect(() => {
    redirectIfRequireAuth();
  });

  useEffect(() => {
    setLocationInfoData();
    setReferralCodeSessionStorage();
  }, []);

  return children;
};

export default LayoutLogicWrapper;
